import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Gift, Plus, CheckCircle2, X, Clock, ScanLine, MessageCircle, Camera } from "lucide-react";
import { toast } from "sonner";
import QrScanner from "./QrScanner";
import { QRCodeSVG } from "qrcode.react";

const EMPTY = { company_id: "", value: "", customer_name: "", customer_phone: "", expires_in_days: "" };

const buildWhatsAppUrl = (phone, company, code, value, expiresAt) => {
  const cleanPhone = (phone || "").replace(/\D/g, "");
  const formattedPhone = cleanPhone.startsWith("55") ? cleanPhone : `55${cleanPhone}`;
  const baseUrl = `${window.location.origin}/v/${code}`;
  const text = encodeURIComponent(
    `🎁 Você recebeu um voucher de B$ ${value.toFixed(2).replace(".", ",")} de *${company}*!\n\n` +
    `Código: *${code}*\n` +
    `Válido até ${new Date(expiresAt).toLocaleDateString("pt-BR")}\n\n` +
    `Para usar, instale o app Morar Bem e adicione à sua carteira:\n${baseUrl}`
  );
  return cleanPhone ? `https://wa.me/${formattedPhone}?text=${text}` : `https://wa.me/?text=${text}`;
};

export default function MerchantVouchers({ companies = [], onRefresh }) {
  const [items, setItems] = useState([]);
  const [localBalanceOverride, setLocalBalanceOverride] = useState(null);
  const [companyId, setCompanyId] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [redeemCode, setRedeemCode] = useState("");
  const [redeemBusy, setRedeemBusy] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [lastIssued, setLastIssued] = useState(null);

  const onScan = (text) => {
    // Accept either raw code XXXX-XXXX or full URL /v/CODE
    let code = (text || "").trim().toUpperCase();
    const m = code.match(/V\/([A-Z0-9-]+)/);
    if (m) code = m[1];
    setRedeemCode(code);
    setScanOpen(false);
    toast.success(`Código lido: ${code}`);
  };

  useEffect(() => {
    if (companies?.[0]?.id && !companyId) setCompanyId(companies[0].id);
  }, [companies, companyId]);

  useEffect(() => {
    if (!companyId) return;
    api.get(`/vouchers/issued/${companyId}`).then((r) => setItems(r.data || [])).catch(() => setItems([]));
  }, [companyId]);

  const company = companies.find((c) => c.id === companyId);
  const voucherBalance = localBalanceOverride !== null ? localBalanceOverride : parseFloat(company?.company_voucher_balance || 0);

  // Reset override when switching company
  useEffect(() => { setLocalBalanceOverride(null); }, [companyId]);

  const openIssue = () => {
    setForm({ ...EMPTY, company_id: companyId, expires_in_days: company?.voucher_default_validity_days || 30 });
    setPreview(null);
    setIssuing(true);
  };

  const doPreview = async () => {
    if (!form.value) return;
    try {
      const r = await api.post("/vouchers/preview", { ...form, value: parseFloat(form.value), expires_in_days: parseInt(form.expires_in_days) || undefined });
      setPreview(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha"); setPreview(null); }
  };

  const confirmIssue = async () => {
    if (!preview) { await doPreview(); return; }
    setBusy(true);
    try {
      const payload = { ...form, value: parseFloat(form.value), expires_in_days: parseInt(form.expires_in_days) || undefined };
      const r = await api.post("/vouchers", payload);
      toast.success(`Voucher emitido: ${r.data.code}`);
      setIssuing(false); setForm(EMPTY); setPreview(null);
      setLastIssued(r.data);  // open share dialog
      // Optimistic local balance update + parent refresh if provided
      setLocalBalanceOverride(voucherBalance - parseFloat(form.value));
      if (onRefresh) onRefresh();
      api.get(`/vouchers/issued/${companyId}`).then((rr) => setItems(rr.data || []));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha"); }
    finally { setBusy(false); }
  };

  const redeem = async () => {
    const code = redeemCode.trim().toUpperCase();
    if (!code) return;
    setRedeemBusy(true);
    try {
      const v = await api.get(`/vouchers/lookup/${code}`);
      if (v.data.status !== "active") {
        toast.error(`Voucher ${v.data.status}`);
      } else if (window.confirm(`Resgatar voucher ${code} no valor de B$ ${v.data.value.toFixed(2).replace(".", ",")}?`)) {
        await api.post(`/vouchers/${code}/redeem`);
        toast.success("Voucher resgatado!");
        setRedeemCode("");
        api.get(`/vouchers/issued/${companyId}`).then((rr) => setItems(rr.data || []));
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha"); }
    finally { setRedeemBusy(false); }
  };

  return (
    <div data-testid="merchant-vouchers">
      <Card className="p-5 mb-5 rounded-2xl border-blue-200 bg-gradient-to-br from-blue-50 to-white">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-blue-900 font-bold">Saldo Voucher</div>
            <div className="font-display text-3xl font-bold text-blue-950" data-testid="voucher-balance">B$ {voucherBalance.toFixed(2).replace(".", ",")}</div>
            <div className="text-xs text-stone-500 mt-1">Use esse saldo para emitir vouchers exclusivos da sua loja.</div>
          </div>
          <Button onClick={openIssue} disabled={!companyId || voucherBalance <= 0} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="voucher-issue-btn">
            <Plus size={16} className="mr-1.5"/>Emitir voucher
          </Button>
        </div>
      </Card>

      <Card className="p-4 mb-5 rounded-2xl border-stone-200">
        <div className="flex items-center gap-2 mb-2"><ScanLine size={18}/><span className="font-display text-lg font-bold">Resgatar voucher na loja</span></div>
        <div className="flex gap-2 flex-wrap">
          <Input value={redeemCode} onChange={(e) => setRedeemCode(e.target.value.toUpperCase())} placeholder="V3K2-9XAB" className="rounded-xl font-mono uppercase max-w-xs flex-1" data-testid="voucher-redeem-input"/>
          <Button type="button" onClick={() => setScanOpen(true)} variant="outline" className="rounded-xl shrink-0" data-testid="voucher-scan-btn" title="Escanear QR do voucher">
            <Camera size={14}/>
          </Button>
          <Button onClick={redeem} disabled={!redeemCode || redeemBusy} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl" data-testid="voucher-redeem-btn">
            {redeemBusy ? "..." : "Resgatar"}
          </Button>
        </div>
      </Card>

      {scanOpen && <QrScanner onScan={onScan} onClose={() => setScanOpen(false)}/>}

      <Card className="rounded-2xl border-stone-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2">Código</th>
                <th className="text-left px-3 py-2">Valor</th>
                <th className="text-left px-3 py-2">Cliente</th>
                <th className="text-left px-3 py-2">Validade</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2">Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.map((v) => (
                <tr key={v.id} className="border-t border-stone-100" data-testid={`voucher-row-${v.code}`}>
                  <td className="px-3 py-2 font-mono font-bold">{v.code}</td>
                  <td className="px-3 py-2">B$ {v.value.toFixed(2).replace(".", ",")}</td>
                  <td className="px-3 py-2 text-stone-600">{v.customer_name || "—"}</td>
                  <td className="px-3 py-2 text-stone-500 text-xs">{new Date(v.expires_at).toLocaleDateString("pt-BR")}</td>
                  <td className="px-3 py-2">
                    {v.status === "active" && <span className="px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-800 text-[10px] font-bold uppercase">Ativo</span>}
                    {v.status === "used" && <span className="px-2 py-0.5 rounded-md bg-stone-200 text-stone-600 text-[10px] font-bold uppercase">Usado</span>}
                    {v.status === "expired" && <span className="px-2 py-0.5 rounded-md bg-red-100 text-red-700 text-[10px] font-bold uppercase">Expirado</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {v.status === "active" && (
                      <button onClick={() => setLastIssued(v)} className="text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1 text-xs font-bold" data-testid={`voucher-share-${v.code}`} title="Enviar pelo WhatsApp">
                        <MessageCircle size={14}/>Enviar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-stone-400 text-sm">Nenhum voucher emitido ainda</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ISSUE DIALOG */}
      {issuing && (
        <Dialog open onOpenChange={(v) => !v && setIssuing(false)}>
          <DialogContent className="rounded-2xl">
            <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><Gift size={20}/>Emitir voucher</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Valor do voucher (R$)</Label><Input type="number" step="0.01" min="0.01" value={form.value} onChange={(e) => { setForm({...form, value: e.target.value}); setPreview(null); }} className="rounded-xl mt-1" data-testid="voucher-value"/></div>
              <div><Label>Vigência (dias)</Label><Input type="number" value={form.expires_in_days} onChange={(e) => { setForm({...form, expires_in_days: e.target.value}); setPreview(null); }} className="rounded-xl mt-1 w-32" data-testid="voucher-days"/></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Nome do cliente (opcional)</Label><Input value={form.customer_name} onChange={(e) => setForm({...form, customer_name: e.target.value})} className="rounded-xl mt-1" data-testid="voucher-customer-name"/></div>
                <div><Label>Telefone (opcional)</Label><Input value={form.customer_phone} onChange={(e) => setForm({...form, customer_phone: e.target.value})} className="rounded-xl mt-1"/></div>
              </div>
              {preview && (
                <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-sm" data-testid="voucher-preview">
                  <div className="flex items-center gap-2 text-emerald-900 font-bold"><CheckCircle2 size={16}/>Confirme a emissão</div>
                  <div className="mt-2 grid grid-cols-2 gap-1 text-xs">
                    <div>Saldo atual: <strong>B$ {preview.current_balance.toFixed(2).replace(".", ",")}</strong></div>
                    <div>Após: <strong>B$ {preview.balance_after.toFixed(2).replace(".", ",")}</strong></div>
                    <div>Validade: <strong>{preview.validity_days} dias</strong></div>
                    <div>Expira: <strong>{new Date(preview.expires_at).toLocaleDateString("pt-BR")}</strong></div>
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setIssuing(false)} className="rounded-xl"><X size={14} className="mr-1"/>Cancelar</Button>
              {!preview ? (
                <Button onClick={doPreview} disabled={!form.value} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="voucher-preview-btn">
                  <Clock size={14} className="mr-1"/>Conferir
                </Button>
              ) : (
                <Button onClick={confirmIssue} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl" data-testid="voucher-confirm-btn">
                  <Gift size={14} className="mr-1"/>{busy ? "Emitindo..." : "Confirmar e emitir"}
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* WHATSAPP SHARE DIALOG */}
      {lastIssued && (
        <Dialog open onOpenChange={(v) => !v && setLastIssued(null)}>
          <DialogContent className="rounded-2xl max-w-md" data-testid="voucher-share-dialog">
            <DialogHeader><DialogTitle className="font-display text-xl flex items-center gap-2"><CheckCircle2 className="text-emerald-600" size={20}/>Voucher emitido!</DialogTitle></DialogHeader>
            <div className="p-4 rounded-xl bg-emerald-50 border-2 border-dashed border-emerald-200 text-center">
              <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">Código</div>
              <div className="font-mono text-3xl font-extrabold text-emerald-900 tracking-wider mt-1" data-testid="voucher-share-code">{lastIssued.code}</div>
              <div className="mt-3 inline-block p-2 bg-white rounded-lg" data-testid="voucher-share-qr">
                <QRCodeSVG value={lastIssued.code} size={128} level="H" includeMargin={false}/>
              </div>
              <div className="text-sm text-stone-700 mt-2">B$ {lastIssued.value.toFixed(2).replace(".", ",")} · Válido até {new Date(lastIssued.expires_at).toLocaleDateString("pt-BR")}</div>
              {lastIssued.customer_name && <div className="text-xs text-stone-500 mt-1">Para: {lastIssued.customer_name}</div>}
            </div>
            <p className="text-sm text-stone-600 mt-3">Envie o link por WhatsApp para o cliente. Ele vai poder se cadastrar no app e adicionar o voucher à carteira dele.</p>
            <a
              href={buildWhatsAppUrl(lastIssued.customer_phone, lastIssued.company_name, lastIssued.code, lastIssued.value, lastIssued.expires_at)}
              target="_blank" rel="noopener noreferrer"
              className="mt-2 block"
              data-testid="voucher-whatsapp-btn"
            >
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl h-12 text-base">
                <MessageCircle size={18} className="mr-2"/>Enviar pelo WhatsApp
              </Button>
            </a>
            <button
              onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/v/${lastIssued.code}`); toast.success("Link copiado!"); }}
              className="w-full mt-2 text-xs text-stone-500 hover:text-stone-800 underline"
              data-testid="voucher-copy-link"
            >
              Ou copiar o link manualmente
            </button>
            <Button variant="ghost" onClick={() => setLastIssued(null)} className="w-full mt-2 rounded-xl">Fechar</Button>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
